/** Clip playback via passthrough element — same VB-Cable path as live mic (v4.75.0). */
import { catWarn } from './catLog';

export const ROUTE_MODE = {
  PASSTHROUGH: 'passthrough',
  DUAL_ELEMENT: 'dual_element',
  // v4.104.0: direct AudioContext.setSinkId render — default engine when supported.
  DIRECT_SINK: 'direct_sink',
};

const RAMP_MS = 50;

/**
 * v4.87.0: PASSTHROUGH is default again. The v4.86.2 switch to DUAL_ELEMENT put
 * two audio elements on the same virtual sink — the garble source patients
 * reported (see docs/soundboard/voicemod-comparison.md). DUAL_ELEMENT stays
 * available as an explicit Studio A/B fallback via writeRouteModePreference.
 */
export function readRouteModePreference() {
  try {
    const v = localStorage.getItem('CATINT_ROUTE_MODE');
    // v4.95.0: one-time migration — machines that lived through the v4.86.2
    // dual-element default may still store it; that pref garbled patient audio.
    if (v === ROUTE_MODE.DUAL_ELEMENT && !localStorage.getItem('CATINT_ROUTE_MODE_MIGRATED')) {
      localStorage.setItem('CATINT_ROUTE_MODE', ROUTE_MODE.PASSTHROUGH);
      localStorage.setItem('CATINT_ROUTE_MODE_MIGRATED', '1');
      return ROUTE_MODE.PASSTHROUGH;
    }
    localStorage.setItem('CATINT_ROUTE_MODE_MIGRATED', '1');
    return v === ROUTE_MODE.DUAL_ELEMENT ? ROUTE_MODE.DUAL_ELEMENT : ROUTE_MODE.PASSTHROUGH;
  } catch {
    return ROUTE_MODE.PASSTHROUGH;
  }
}

export function writeRouteModePreference(mode) {
  try {
    localStorage.setItem('CATINT_ROUTE_MODE', mode);
  } catch (_) {}
}

/**
 * Decode blob to AudioBuffer. Caller must close ctx when done.
 * @param {Blob} blob
 * @param {typeof AudioContext} [AudioContextCtor]
 */
export async function decodeBlobToBuffer(blob, AudioContextCtor = window.AudioContext || window.webkitAudioContext) {
  if (!AudioContextCtor) throw new Error('AudioContext unavailable');
  const arrayBuffer = await blob.arrayBuffer();
  const ctx = new AudioContextCtor();
  const buffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  return { ctx, buffer };
}

/**
 * Peak-normalize an AudioBuffer in place to targetDbFS (default −1 dB).
 * v4.95.0: clips previously played at whatever level they were recorded at,
 * so "sounds fine to me" proved nothing about the patient path.
 */
export function normalizePeak(buffer, targetDb = -1) {
  let peak = 0;
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      const abs = Math.abs(data[i]);
      if (abs > peak) peak = abs;
    }
  }
  if (peak <= 0.00001) return buffer; // silence — nothing to scale
  const target = Math.pow(10, targetDb / 20);
  const scale = target / peak;
  if (scale > 0.98 && scale < 1.02) return buffer; // already there
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < data.length; i++) data[i] *= scale;
  }
  return buffer;
}

/**
 * Play buffer through passthrough element (replaces srcObject; restores on end).
 * @returns {{ promise: Promise<void>, stop: () => void, duration: number }}
 */
export function playBufferViaPassthrough(passthroughEl, buffer, ctx, {
  volume = 1,
  sinkId,
  savedSrcObject = null,
  onProgress,
  normalize = true,
}) {
  if (!passthroughEl || !buffer || !ctx) {
    return { promise: Promise.reject(new Error('missing passthrough args')), stop: () => {}, duration: 0 };
  }

  let stopped = false;
  let resolveEnd;
  let rejectEnd;
  const promise = new Promise((res, rej) => {
    resolveEnd = res;
    rejectEnd = rej;
  });

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const dest = ctx.createMediaStreamDestination();
  source.connect(gain);
  gain.connect(dest);

  const finish = (err) => {
    if (stopped) return;
    stopped = true;
    try {
      source.stop();
    } catch (_) {}
    try {
      gain.gain.setValueAtTime(0, ctx.currentTime);
    } catch (_) {}
    passthroughEl.srcObject = savedSrcObject;
    passthroughEl.pause?.();
    ctx.close().catch(() => {});
    if (err) rejectEnd(err);
    else resolveEnd();
  };

  const stop = () => finish();

  const bindAndPlay = async () => {
    try {
      if (sinkId && passthroughEl.setSinkId) {
        await passthroughEl.setSinkId(sinkId);
      }
      passthroughEl.srcObject = dest.stream;
      passthroughEl.volume = 1;
      passthroughEl.muted = false;
      // v4.95.0: autoplay rejection was swallowed here (.catch(() => {})) —
      // the patient simply heard nothing. NotAllowedError is fatal: surface it
      // so callers can show a warning / fall back. Other errors (e.g. AbortError
      // from a rapid srcObject swap) are logged and playback continues.
      try {
        await passthroughEl.play();
      } catch (err) {
        if (err?.name === 'NotAllowedError') {
          throw new Error('autoplay_blocked');
        }
        catWarn('[AudioRoute] passthrough play() warning:', err);
      }

      if (normalize) normalizePeak(buffer);

      const now = ctx.currentTime;
      gain.gain.linearRampToValueAtTime(volume, now + RAMP_MS / 1000);
      source.start(0);

      const duration = buffer.duration;
      let progId = null;
      if (onProgress) {
        const t0 = performance.now();
        const tick = () => {
          if (stopped) return;
          const p = Math.min(1, (performance.now() - t0) / 1000 / duration);
          onProgress(p);
          if (p < 1) progId = requestAnimationFrame(tick);
        };
        progId = requestAnimationFrame(tick);
      }

      source.onended = () => {
        if (progId) cancelAnimationFrame(progId);
        onProgress?.(1);
        finish();
      };
    } catch (e) {
      finish(e);
    }
  };

  bindAndPlay();

  return { promise, stop, duration: buffer.duration };
}

/**
 * High-level: blob → passthrough injection.
 */
export async function playBlobViaPassthrough(passthroughEl, blob, opts = {}) {
  const { ctx, buffer } = await decodeBlobToBuffer(blob);
  const session = playBufferViaPassthrough(passthroughEl, buffer, ctx, opts);
  await session.promise;
  return { mode: ROUTE_MODE.PASSTHROUGH, duration: session.duration };
}
